import { DATE_FORMAT_LOCALE, DATE_FORMAT_OPTIONS, DATE_TIME_FORMAT_OPTIONS } from '../styles/theme/theme';

const LBS_TO_KG = 0.453592;
const KG_TO_LBS = 2.20462;

const BACKEND_WEIGHT_UNIT = 'lbs';
const BACKEND_CURRENCY = 'USD';

export const formatWeight = (
  weight: number | string | undefined,
  locale: string = 'en-US',
  targetUnit: 'lbs' | 'kg' = 'lbs',
  sourceUnit: 'lbs' | 'kg' = BACKEND_WEIGHT_UNIT
): string => {
  if (weight === undefined || weight === null || weight === '') {
    return 'N/A';
  }

  const numericWeight = typeof weight === 'string' ? parseFloat(weight) : weight;

  if (isNaN(numericWeight)) {
    return 'N/A';
  }

  let convertedWeight = numericWeight;

  if (sourceUnit !== targetUnit) {
    if (sourceUnit === 'lbs' && targetUnit === 'kg') {
      convertedWeight = numericWeight * LBS_TO_KG;
    } else if (sourceUnit === 'kg' && targetUnit === 'lbs') {
      convertedWeight = numericWeight * KG_TO_LBS;
    }
  }

  const formattedNumber = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(convertedWeight);

  return `${formattedNumber} ${targetUnit}`;
};

let EXCHANGE_RATES: Record<string, number> = {
  'USD': 1.0,
  'EUR': 0.92,
  'GBP': 0.79,
  'CAD': 1.36,
  'AUD': 1.52,
  'JPY': 149.5,
  'INR': 83.12,
  'CNY': 7.24,
};

export const updateExchangeRates = (rates: Record<string, number>): void => {
  EXCHANGE_RATES = { ...EXCHANGE_RATES, ...rates };
};

export const formatPrice = (
  price: number | string | undefined,
  locale: string = 'en-US',
  targetCurrency: string = 'USD',
  sourceCurrency: string = BACKEND_CURRENCY,
  convertCurrency: boolean = true
): string => {
  if (price === undefined || price === null || price === '') {
    return 'N/A';
  }

  const numericPrice = typeof price === 'string' ? parseFloat(price) : price;

  if (isNaN(numericPrice)) {
    return 'N/A';
  }

  let convertedPrice = numericPrice;

  if (convertCurrency && sourceCurrency !== targetCurrency) {
    const sourceRate = EXCHANGE_RATES[sourceCurrency] || 1;
    const targetRate = EXCHANGE_RATES[targetCurrency] || 1;
    const priceInUSD = numericPrice / sourceRate;
    convertedPrice = priceInUSD * targetRate;
  }

  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: targetCurrency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(convertedPrice);
  } catch {
    return `${convertedPrice.toFixed(2)} ${targetCurrency}`;
  }
};

export const formatDateTime = (
  dateString?: string,
  timeString?: string,
  _locale: string = 'en-US',
  includeTime: boolean = !!timeString,
  timeZone: string = 'UTC'
): string => {
  if (!dateString) {
    return 'N/A';
  }

  try {
    let date: Date;

    if (timeString) {
      const timeParts = timeString.split(':');
      const hours = timeParts[0]?.padStart(2, '0') || '00';
      const minutes = timeParts[1]?.padStart(2, '0') || '00';
      const seconds = timeParts[2]?.padStart(2, '0') || '00';
      const isoString = `${dateString}T${hours}:${minutes}:${seconds}Z`;
      date = new Date(isoString);
    } else {
      if (dateString.includes('T')) {
        date = new Date(dateString);
      } else {
        date = new Date(`${dateString}T00:00:00Z`);
      }
    }

    if (isNaN(date.getTime())) {
      return 'N/A';
    }

    if (includeTime) {
      return new Intl.DateTimeFormat(DATE_FORMAT_LOCALE, {
        ...DATE_TIME_FORMAT_OPTIONS,
        timeZone,
      }).format(date);
    } else {
      return new Intl.DateTimeFormat(DATE_FORMAT_LOCALE, {
        ...DATE_FORMAT_OPTIONS,
        timeZone,
      }).format(date);
    }
  } catch {
    return 'N/A';
  }
};

export const getUserLocale = (): string => {
  const storedLocale = localStorage.getItem('userLocale');
  if (storedLocale) {
    return storedLocale;
  }
  return navigator.language || 'en-US';
};

export const getUserWeightUnit = (): 'lbs' | 'kg' => {
  const storedUnit = localStorage.getItem('weightUnit');
  if (storedUnit === 'kg' || storedUnit === 'lbs') {
    return storedUnit;
  }
  return 'lbs';
};

export const getUserCurrency = (): string => {
  const storedCurrency = localStorage.getItem('currency');
  if (storedCurrency) {
    return storedCurrency;
  }
  return 'USD';
};

export const getLocaleDateFormat = (locale: string = 'en-US'): string => {
  if (locale.startsWith('en-US') || locale.startsWith('en-CA')) {
    return 'MM/DD/YYYY';
  }

  if (locale.startsWith('ja') || locale.startsWith('ko') || locale.startsWith('zh') ||
      locale.startsWith('hu') || locale.startsWith('lt') || locale.startsWith('mn')) {
    return 'YYYY/MM/DD';
  }

  return 'DD/MM/YYYY';
};

export const getLocaleDatePlaceholder = (locale: string = 'en-US'): string => {
  const format = getLocaleDateFormat(locale);
  return format.toLowerCase();
};

const MONTH_ABBRS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const FULL_MONTH_NAMES = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
];

export const applyDateMask = (value: string, locale: string = 'en-US'): string => {
  // For birthday picker (en-GB locale) use MMM DD, YYYY format: "Nov 23, 2026"
  if (locale === 'en-GB') {
    const trimmed = value.trimStart();
    if (!trimmed) return '';

    // Case 1: Input starts with letters — user is typing month as text
    // e.g. "n", "no", "nov", "Nov 23", "Nov 23, 2026"
    // STRICT: only 3 letter characters allowed for month; any letters beyond 3 are stripped
    if (/^[A-Za-z]/.test(trimmed)) {
      // Extract the leading letter segment (max 3 chars) and any trailing numeric parts
      const textMatch = trimmed.match(/^([A-Za-z]{1,3})[A-Za-z]*\s*(\d{0,2})\s*,?\s*(\d{0,4})$/);
      if (!textMatch) return '';

      const [, monthText, dayPart, yearPart] = textMatch;
      // Cap month text to 3 characters
      const cappedMonth = monthText.slice(0, 3);
      const lowerMonth = cappedMonth.toLowerCase();

      // If fewer than 3 letters typed, return only those letters (still typing month)
      if (cappedMonth.length < 3) {
        return cappedMonth;
      }

      // Exactly 3 letters — resolve to month abbreviation
      // Try exact 3-char abbreviation match
      let monthIndex = MONTH_ABBRS.findIndex((m) => m.toLowerCase() === lowerMonth);

      // Try prefix match on full month names (e.g. "nov" → november → index 10)
      if (monthIndex === -1) {
        monthIndex = FULL_MONTH_NAMES.findIndex((m) => m.startsWith(lowerMonth));
      }

      if (monthIndex !== -1) {
        const monthAbbr = MONTH_ABBRS[monthIndex];
        if (!dayPart) return monthAbbr;
        if (!yearPart) return `${monthAbbr} ${dayPart}`;
        return `${monthAbbr} ${dayPart}, ${yearPart}`;
      }

      // Invalid 3-char month — return just the 3 chars (user may correct)
      return cappedMonth;
    }

    // Case 2: Input is all digits — treat as MMDDYYYY stream
    const digits = trimmed.replace(/\D/g, '');
    if (digits.length === 0) return '';

    const monthStr = digits.slice(0, 2);
    const dayStr = digits.slice(2, 4);
    const yearStr = digits.slice(4, 8);

    const monthNum = parseInt(monthStr, 10);
    const validMonth = monthNum >= 1 && monthNum <= 12;
    const monthAbbr = validMonth ? MONTH_ABBRS[monthNum - 1] : monthStr;

    if (digits.length < 2) return digits;
    if (digits.length === 2) return monthAbbr;
    if (digits.length <= 4) return `${monthAbbr} ${dayStr}`;
    return `${monthAbbr} ${dayStr}, ${yearStr}`;
  }

  const digits = value.replace(/\D/g, '');
  const format = getLocaleDateFormat(locale);
  const separator = format.includes('/') ? '/' : '-';

  if (format === 'MM/DD/YYYY' || format === 'DD/MM/YYYY') {
    if (digits.length === 0) return '';
    if (digits.length <= 2) return digits;
    if (digits.length <= 4) return `${digits.slice(0, 2)}${separator}${digits.slice(2)}`;
    return `${digits.slice(0, 2)}${separator}${digits.slice(2, 4)}${separator}${digits.slice(4, 8)}`;
  } else if (format === 'YYYY/MM/DD') {
    if (digits.length === 0) return '';
    if (digits.length <= 4) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 4)}${separator}${digits.slice(4)}`;
    return `${digits.slice(0, 4)}${separator}${digits.slice(4, 6)}${separator}${digits.slice(6, 8)}`;
  }

  return value;
};

export const parseLocaleDateToISO = (dateString: string, locale: string = 'en-US'): string | null => {
  if (!dateString) return null;

  // Handle MMM DD, YYYY or full month name format (used by birthday picker with en-GB locale)
  if (locale === 'en-GB') {
    // e.g. "Nov 23, 2026" or "November 23, 2026"
    const match = dateString.match(/^([A-Za-z]+)\s+(\d{1,2}),?\s*(\d{4})$/);
    if (!match) return null;
    const [, monthText, dayStr, yearStr] = match;
    const lowerMonth = monthText.toLowerCase();
    // Try abbreviation first, then full name
    let monthIndex = MONTH_ABBRS.findIndex((m) => m.toLowerCase() === lowerMonth);
    if (monthIndex === -1) monthIndex = FULL_MONTH_NAMES.findIndex((m) => m === lowerMonth);
    if (monthIndex === -1) return null;
    const month = String(monthIndex + 1).padStart(2, '0');
    const day = dayStr.padStart(2, '0');
    const year = yearStr;
    const yearNum = parseInt(year);
    const dayNum = parseInt(day);
    if (yearNum < 1900 || yearNum > 2100) return null;
    if (dayNum < 1 || dayNum > 31) return null;
    const isoDate = `${year}-${month}-${day}`;
    const date = new Date(isoDate);
    if (isNaN(date.getTime())) return null;
    return isoDate;
  }

  const format = getLocaleDateFormat(locale);
  const separator = dateString.includes('/') ? '/' : dateString.includes('-') ? '-' : null;

  if (!separator) return null;

  const parts = dateString.split(separator);

  if (parts.length !== 3) return null;

  let year: string, month: string, day: string;

  if (format === 'MM/DD/YYYY') {
    [month, day, year] = parts;
  } else if (format === 'YYYY/MM/DD') {
    [year, month, day] = parts;
  } else {
    [day, month, year] = parts;
  }

  const yearNum = parseInt(year);
  const monthNum = parseInt(month);
  const dayNum = parseInt(day);

  if (isNaN(yearNum) || isNaN(monthNum) || isNaN(dayNum)) {
    return null;
  }

  if (year.length !== 4 || yearNum < 1900 || yearNum > 2100) {
    return null;
  }

  if (monthNum < 1 || monthNum > 12) {
    return null;
  }

  if (dayNum < 1 || dayNum > 31) {
    return null;
  }

  const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;

  const date = new Date(isoDate);
  if (isNaN(date.getTime())) {
    return null;
  }

  return isoDate;
};

export const formatISOToLocaleDate = (isoDate: string, locale: string = 'en-US'): string => {
  if (!isoDate) return '';

  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return '';

  // Birthday picker uses en-GB → MMM DD, YYYY
  if (locale === 'en-GB') {
    const monthAbbr = MONTH_ABBRS[date.getMonth()];
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    return `${monthAbbr} ${day}, ${year}`;
  }

  const year = date.getFullYear().toString();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');

  const format = getLocaleDateFormat(locale);
  const separator = format.includes('/') ? '/' : '-';

  if (format === 'MM/DD/YYYY') {
    return `${month}${separator}${day}${separator}${year}`;
  } else if (format === 'YYYY/MM/DD') {
    return `${year}${separator}${month}${separator}${day}`;
  } else {
    return `${day}${separator}${month}${separator}${year}`;
  }
};

export const formatDateToISO = (date: Date | null | undefined): string => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) {
    return '';
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
};

export const getTodayISO = (): string => {
  return formatDateToISO(new Date());
};

export const parseISODate = (dateString: string | null | undefined): Date | null => {
  if (!dateString || typeof dateString !== 'string') {
    return null;
  }

  const parts = dateString.split('-');
  if (parts.length !== 3) {
    return null;
  }

  const year = parseInt(parts[0], 10);
  const month = parseInt(parts[1], 10) - 1;
  const day = parseInt(parts[2], 10);

  if (isNaN(year) || isNaN(month) || isNaN(day)) {
    return null;
  }

  const date = new Date(year, month, day, 0, 0, 0, 0);

  if (isNaN(date.getTime())) {
    return null;
  }

  return date;
};

export const convertToUTC = (dateString?: string, timeString?: string): string | null => {
  if (!dateString) return null;

  try {
    const timeParts = (timeString || '00:00').split(':');
    const hours = parseInt(timeParts[0] || '0', 10);
    const minutes = parseInt(timeParts[1] || '0', 10);
    const seconds = parseInt(timeParts[2] || '0', 10);

    const dateParts = dateString.split('-');
    if (dateParts.length !== 3) return null;

    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1;
    const day = parseInt(dateParts[2], 10);

    const localDate = new Date(year, month, day, hours, minutes, seconds);

    if (isNaN(localDate.getTime())) return null;

    return localDate.toISOString();
  } catch {
    return null;
  }
};

export const extractUTCDate = (utcIsoString: string): string => {
  try {
    const date = new Date(utcIsoString);
    if (isNaN(date.getTime())) return '';

    const year = date.getUTCFullYear();
    const month = String(date.getUTCMonth() + 1).padStart(2, '0');
    const day = String(date.getUTCDate()).padStart(2, '0');

    return `${year}-${month}-${day}`;
  } catch {
    return '';
  }
};

export const extractUTCTime = (utcIsoString: string): string => {
  try {
    const date = new Date(utcIsoString);
    if (isNaN(date.getTime())) return '';

    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');

    return `${hours}:${minutes}`;
  } catch {
    return '';
  }
};

// Converts UTC date/time strings to a local Date object.
// The returned Date, when formatted with Intl.DateTimeFormat and user's timezone,
// will correctly show the local time.
export const parseUTCDateTime = (utcDateString?: string, utcTimeString?: string): Date | null => {
  if (!utcDateString) return null;

  try {
    const timeParts = (utcTimeString || '00:00').split(':');
    const hours = parseInt(timeParts[0] || '0', 10);
    const minutes = parseInt(timeParts[1] || '0', 10);
    const seconds = parseInt(timeParts[2] || '0', 10);

    const dateParts = utcDateString.split('-');
    if (dateParts.length !== 3) return null;

    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1;
    const day = parseInt(dateParts[2], 10);

    const utcTimestamp = Date.UTC(year, month, day, hours, minutes, seconds);

    if (isNaN(utcTimestamp)) return null;

    return new Date(utcTimestamp);
  } catch {
    return null;
  }
};

export const convertDateToUTC = (dateString?: string): string | null => {
  if (!dateString) return null;

  const utcIso = convertToUTC(dateString, '00:00');
  if (!utcIso) return null;

  return extractUTCDate(utcIso);
};

export const combineDateTime = (date: string, time: string): Date | null => {
  if (!date || !time) return null;

  try {
    const [hours, minutes] = time.split(':').map(Number);
    const parsedDate = parseISODate(date);

    if (!parsedDate || isNaN(hours) || isNaN(minutes)) {
      return null;
    }

    parsedDate.setHours(hours, minutes, 0, 0);
    return parsedDate;
  } catch {
    return null;
  }
};

// Ensures time string is in HH:mm format, stripping seconds if present
export const ensureTimeFormat = (time: string | undefined, fallback?: string | undefined): string => {
  const timeValue = time || fallback || '';
  if (!timeValue) return '';

  if (/^\d{2}:\d{2}:\d{2}$/.test(timeValue)) {
    return timeValue.substring(0, 5);
  }

  if (/^\d{2}:\d{2}$/.test(timeValue)) {
    return timeValue;
  }

  if (/^\d{1}:\d{2}$/.test(timeValue)) {
    return '0' + timeValue;
  }

  if (/^\d{1}:\d{2}:\d{2}$/.test(timeValue)) {
    return '0' + timeValue.substring(0, 4);
  }

  return timeValue;
};

export interface ExpiryCapsule {
  variant: 'expired' | 'hoursMinutes' | 'hours' | 'days';
  hours?: number;
  minutes?: number;
  days?: number;
  background: string;
  border: string;
  color: string;
}

export const getExpiryCapsule = (
  expiresAt?: string | null,
  dropOfDate?: string | null,
  dropOfTime?: string | null
): ExpiryCapsule | null => {
  if (!expiresAt) return null;

  let expiryDate = new Date(expiresAt);
  if (isNaN(expiryDate.getTime())) return null;

  const diffFromExpiresAtMs = expiryDate.getTime() - Date.now();
  const diffFromExpiresAtDays = diffFromExpiresAtMs / (1000 * 60 * 60 * 24);

  if (diffFromExpiresAtDays < 7 && dropOfDate) {
    const { localDate, localTime } = convertUTCToLocalDateTime(dropOfDate, dropOfTime ?? '23:59:59');
    if (localDate) {
      const dropOfDateTime = new Date(`${localDate}T${localTime ?? '23:59:59'}`);
      // Drop-off falling inside the withdraw window (on/before expires_at) means the
      // drop-off deadline is the binding constraint, so use it instead of expires_at.
      // If drop-off falls outside the window (after expires_at), expires_at stays correct.
      if (!isNaN(dropOfDateTime.getTime()) && dropOfDateTime.getTime() <= expiryDate.getTime()) {
        expiryDate = dropOfDateTime;
      }
    }
  }

  const diffMs = expiryDate.getTime() - Date.now();
  if (diffMs <= 0) {
    return { variant: 'expired', background: '#FDEAEA', border: '#E62E2E', color: '#E62E2E' };
  }

  const diffHours = diffMs / (1000 * 60 * 60);

  if (diffHours < 24) {
    const hours = Math.floor(diffHours);
    const minutes = Math.floor((diffMs / (1000 * 60)) % 60);
    return {
      variant: 'hoursMinutes',
      hours,
      minutes,
      background: '#FDEAEA',
      border: '#E62E2E',
      color: '#E62E2E',
    };
  }

  if (diffHours < 72) {
    return {
      variant: 'hours',
      hours: Math.round(diffHours),
      background: '#FEF6EE',
      border: '#F9DBAF',
      color: '#B93815',
    };
  }

  return {
    variant: 'days',
    days: Math.round(diffHours / 24),
    background: '#FAFAFA',
    border: '#E9EAEB',
    color: '#414651',
  };
};

export const convertUTCToLocalDateTime = (
  utcDateString?: string,
  utcTimeString?: string
): { localDate: string | null; localTime: string | null } => {
  if (!utcDateString) {
    return { localDate: null, localTime: null };
  }

  try {
    const utcDate = parseUTCDateTime(utcDateString, utcTimeString);
    if (!utcDate) {
      return { localDate: null, localTime: null };
    }

    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');
    const localDate = `${year}-${month}-${day}`;

    const hours = String(utcDate.getHours()).padStart(2, '0');
    const minutes = String(utcDate.getMinutes()).padStart(2, '0');
    const localTime = `${hours}:${minutes}`;

    return { localDate, localTime };
  } catch {
    return { localDate: null, localTime: null };
  }
};
